import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CameraOff, FileWarning, HeartPulse } from "lucide-react";
import Tooltip from "../common/Tooltip";
import Button from "../ui/Button";
import { RosterFlags, RosterFlagsFetcher } from "../../api/rosterFlagsAPI";
import { useRosterFlags, UseRosterFlags } from "../../hooks/useRosterFlags";

/**
 * ROSTER FLAGS — the one piece every roster uses to show what a coach must
 * not miss (Chris, 2026-10-07): a medical condition or allergy, "do not
 * photograph", and a required waiver the family has not signed.
 *
 *   <RosterFlagsScope athleteIds={…} registrationIds={…}>   one read per roster
 *     … <RosterFlagRow athleteId={row.id}>                  hides the row while a filter excludes it
 *         … <RosterFlagBadges athleteId={row.id} /> …       one per person
 *       </RosterFlagRow> …
 *   </RosterFlagsScope>
 *
 * Class attendance, the class roster, camp / clinic / event attendance and
 * their registration lists all render through here and all ask the same
 * endpoint, so two screens cannot disagree about the same child.
 *
 * The scope also puts ONE LINE above the roster ("3 medical alerts · 2 no
 * photos · 1 waiver missing") counted from the same answer the badges use.
 * Each part is a button that narrows the roster to those people. With nothing
 * to count the line is not there at all.
 *
 * A person with nothing to flag renders NOTHING — a clean roster stays clean.
 * A roster whose flags could not be read says so (it must never look clean).
 *
 * Each badge is a symbol in a fixed color (medical red, no photos amber,
 * waiver missing blue-gray). These are warning colors, deliberately NOT the
 * club's brand color. The words stay beside the symbol on a wide screen; on a
 * narrow one only the symbol shows, and the words are still its accessible
 * name and its tooltip. Nothing blinks.
 *
 * Medical detail appears in the badge's tooltip only when the backend sent
 * it, which it does only for someone who can already open the athlete's
 * medical information. Everyone else sees that an alert exists.
 */

export type RosterFlagKind = "medical" | "no_photos" | "waiver_missing";

export interface RosterFlagSubjectRef {
  athleteId?: string | null;
  registrationId?: string | null;
}

export interface RosterFlagCounts {
  medical: number;
  no_photos: number;
  waiver_missing: number;
}

export interface RosterFlagsScopeValue extends UseRosterFlags {
  /** The flag the roster is narrowed to, or null for everyone. */
  filter: RosterFlagKind | null;
  setFilter: (next: RosterFlagKind | null) => void;
  /** How many people on the roster carry each flag. */
  counts: RosterFlagCounts;
  /** This person's flags, or null. */
  flagsFor: (subject: RosterFlagSubjectRef | null | undefined) => RosterFlags | null;
  /** Should this person be listed under the current filter? Always true with no filter. */
  matches: (subject: RosterFlagSubjectRef | null | undefined) => boolean;
}

interface ScopeSubjects {
  athleteIds?: Array<string | null | undefined>;
  registrationIds?: Array<string | null | undefined>;
  /**
   * The people on screen right now, when the roster is showing only part of
   * the list the flags were read for (a search, a status filter). The summary
   * counts these. Left out, it counts everyone the flags were read for.
   */
  shown?: {
    athleteIds?: Array<string | null | undefined>;
    registrationIds?: Array<string | null | undefined>;
  };
}

const KINDS: RosterFlagKind[] = ["medical", "no_photos", "waiver_missing"];

/**
 * One roster's flags, the summary counts and the filter. Most rosters let
 * `RosterFlagsScope` call this for them; a roster that pages its rows calls
 * it itself so it can narrow the list BEFORE paging, then hands the value to
 * `<RosterFlagsScope scope={…}>`.
 */
export function useRosterFlagsScope(subjects: ScopeSubjects, fetcher?: RosterFlagsFetcher): RosterFlagsScopeValue {
  const flags = useRosterFlags({ athleteIds: subjects.athleteIds, registrationIds: subjects.registrationIds }, fetcher);
  const [filter, setFilter] = useState<RosterFlagKind | null>(null);
  const result = flags.result;

  const flagsFor = useCallback(
    (subject: RosterFlagSubjectRef | null | undefined): RosterFlags | null => {
      if (!result || !subject) return null;
      return (
        (subject.registrationId && result.registrations[subject.registrationId]) ||
        (subject.athleteId && result.athletes[subject.athleteId]) ||
        null
      );
    },
    [result]
  );

  const shownAthletes = (subjects.shown?.athleteIds ?? subjects.athleteIds ?? []).filter(Boolean).join(",");
  const shownRegistrations = (subjects.shown?.registrationIds ?? subjects.registrationIds ?? [])
    .filter(Boolean)
    .join(",");

  const counts = useMemo<RosterFlagCounts>(() => {
    const out: RosterFlagCounts = { medical: 0, no_photos: 0, waiver_missing: 0 };
    if (!result) return out;
    const add = (found?: RosterFlags) => {
      if (!found) return;
      for (const kind of KINDS) if (found[kind]) out[kind] += 1;
    };
    Array.from(new Set(shownRegistrations ? shownRegistrations.split(",") : [])).forEach((id) =>
      add(result.registrations[id])
    );
    Array.from(new Set(shownAthletes ? shownAthletes.split(",") : [])).forEach((id) => add(result.athletes[id]));
    return out;
  }, [result, shownAthletes, shownRegistrations]);

  // A filter nobody matches any more (the list changed, the flags were
  // re-read) would leave an empty roster with no way to see why: drop it.
  const state = flags.state;
  useEffect(() => {
    if (!filter) return;
    if (state === "error" || (state === "ready" && counts[filter] === 0)) setFilter(null);
  }, [filter, counts, state]);

  const matches = useCallback(
    (subject: RosterFlagSubjectRef | null | undefined) => {
      if (!filter) return true;
      return flagsFor(subject)?.[filter] === true;
    },
    [filter, flagsFor]
  );

  return useMemo(
    () => ({ ...flags, filter, setFilter, counts, flagsFor, matches }),
    [flags, filter, counts, flagsFor, matches]
  );
}

const RosterFlagsContext = createContext<RosterFlagsScopeValue | null>(null);

interface ScopeProps extends ScopeSubjects {
  /** A screen not signed in as staff (the kiosk) reads the flags its own way. */
  fetcher?: RosterFlagsFetcher;
  /** A value from `useRosterFlagsScope`, for a roster that pages its rows. */
  scope?: RosterFlagsScopeValue;
  /** Spacing for the summary line where the roster sits inside a padded box. */
  summaryClassName?: string;
  children: React.ReactNode;
}

const ScopeFrame: React.FC<{ scope: RosterFlagsScopeValue; summaryClassName?: string; children: React.ReactNode }> = ({
  scope,
  summaryClassName,
  children,
}) => (
  <RosterFlagsContext.Provider value={scope}>
    {scope.state === "error" && (
      <div
        role="alert"
        className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100"
      >
        <span>Medical, photo and waiver alerts could not be checked for this list. Do not assume it is clear.</span>
        <Button type="button" variant="secondary" size="xs" onClick={scope.retry}>
          Try again
        </Button>
      </div>
    )}
    <RosterFlagsSummary className={summaryClassName} />
    {children}
  </RosterFlagsContext.Provider>
);

const OwnScope: React.FC<ScopeProps> = ({ fetcher, summaryClassName, children, athleteIds, registrationIds, shown }) => {
  const scope = useRosterFlagsScope({ athleteIds, registrationIds, shown }, fetcher);
  return (
    <ScopeFrame scope={scope} summaryClassName={summaryClassName}>
      {children}
    </ScopeFrame>
  );
};

export const RosterFlagsScope: React.FC<ScopeProps> = (props) =>
  props.scope ? (
    <ScopeFrame scope={props.scope} summaryClassName={props.summaryClassName}>
      {props.children}
    </ScopeFrame>
  ) : (
    <OwnScope {...props} />
  );

// ── What each flag looks like. Fixed warning colors, never the brand color. ──

interface KindStyle {
  testId: string;
  label: string;
  Icon: React.ComponentType<any>;
  /** The badge beside a name. */
  pill: string;
  /** The summary button, not selected / selected. */
  chip: string;
  chipOn: string;
  count: (n: number) => string;
}

const STYLES: Record<RosterFlagKind, KindStyle> = {
  medical: {
    testId: "roster-flag-medical",
    label: "Medical",
    Icon: HeartPulse,
    pill: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200",
    chip: "border-red-300 text-red-800 hover:bg-red-50 dark:border-red-700 dark:text-red-200 dark:hover:bg-red-900/30",
    chipOn: "border-red-700 bg-red-700 text-white dark:border-red-500 dark:bg-red-600",
    count: (n) => `${n} medical ${n === 1 ? "alert" : "alerts"}`,
  },
  no_photos: {
    testId: "roster-flag-no-photos",
    label: "No photos",
    Icon: CameraOff,
    pill: "bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100",
    chip: "border-amber-400 text-amber-900 hover:bg-amber-50 dark:border-amber-600 dark:text-amber-100 dark:hover:bg-amber-900/30",
    chipOn: "border-amber-500 bg-amber-500 text-black dark:border-amber-400 dark:bg-amber-400",
    count: (n) => `${n} no photos`,
  },
  waiver_missing: {
    testId: "roster-flag-waiver-missing",
    label: "Waiver missing",
    Icon: FileWarning,
    pill: "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100",
    chip: "border-slate-400 text-slate-800 hover:bg-slate-100 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-700",
    chipOn: "border-slate-700 bg-slate-700 text-white dark:border-slate-300 dark:bg-slate-300 dark:text-slate-900",
    count: (n) => `${n} ${n === 1 ? "waiver" : "waivers"} missing`,
  },
};

/**
 * The one-line answer to "do we need to know anything about anybody on this
 * list?" Rendered by the scope; nothing when every count is zero.
 */
export const RosterFlagsSummary: React.FC<{ className?: string }> = ({ className = "mb-3" }) => {
  const scope = useContext(RosterFlagsContext);
  if (!scope || scope.state !== "ready") return null;
  const present = KINDS.filter((kind) => scope.counts[kind] > 0);
  if (present.length === 0) return null;

  return (
    <div
      data-testid="roster-flags-summary"
      role="group"
      aria-label="Alerts on this list"
      className={`flex flex-wrap items-center gap-x-1 gap-y-2 text-sm ${className}`}
    >
      {present.map((kind, index) => {
        const style = STYLES[kind];
        const on = scope.filter === kind;
        return (
          <React.Fragment key={kind}>
            {index > 0 && (
              <span aria-hidden="true" className="px-1 text-gray-400">
                ·
              </span>
            )}
            <button
              type="button"
              data-testid={`roster-flags-summary-${kind}`}
              aria-pressed={on}
              title={on ? "Showing only these. Click to show everyone." : "Show only these"}
              onClick={(e) => {
                e.stopPropagation();
                scope.setFilter(on ? null : kind);
              }}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-semibold focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-500 ${
                on ? style.chipOn : style.chip
              }`}
            >
              <style.Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {style.count(scope.counts[kind])}
            </button>
          </React.Fragment>
        );
      })}
      {scope.filter && (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          data-testid="roster-flags-summary-clear"
          onClick={() => scope.setFilter(null)}
        >
          Show everyone
        </Button>
      )}
    </div>
  );
};

const PILL =
  "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold whitespace-nowrap cursor-help";
const ICON = "h-4 w-4 flex-shrink-0";

const Badge: React.FC<{ kind: RosterFlagKind; tooltip: React.ReactNode }> = ({ kind, tooltip }) => {
  const style = STYLES[kind];
  return (
    <Tooltip content={tooltip} inline>
      <span
        data-testid={style.testId}
        tabIndex={0}
        // A roster row is often clickable (it opens the athlete). Tapping a
        // badge must show what the badge means, not open the row.
        onClick={(e) => e.stopPropagation()}
        className={`${PILL} ${style.pill}`}
      >
        <style.Icon className={ICON} aria-hidden="true" />
        {/* Always the badge's name for a screen reader; visible from the
            small breakpoint up, symbol only below it. */}
        <span className="sr-only sm:not-sr-only">{style.label}</span>
      </span>
    </Tooltip>
  );
};

function medicalTooltip(flags: RosterFlags): React.ReactNode {
  if (flags.medical_detail && flags.medical_detail.length > 0) {
    return (
      <span className="block max-w-xs text-left">
        {flags.medical_detail.map((line, i) => (
          <span key={i} className="block">
            <strong>{line.label}:</strong> {line.text}
          </span>
        ))}
      </span>
    );
  }
  return "Medical information or an allergy is on file for this person. Ask a manager for the details.";
}

/** The badges for flags you already hold. Renders nothing for no flags. */
export const RosterFlagPills: React.FC<{ flags?: RosterFlags | null; className?: string }> = ({
  flags,
  className = "",
}) => {
  if (!flags || !(flags.medical || flags.no_photos || flags.waiver_missing)) return null;
  const missing = flags.missing_waivers || [];
  return (
    <span data-testid="roster-flags" className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      {flags.medical && <Badge kind="medical" tooltip={medicalTooltip(flags)} />}
      {flags.no_photos && (
        <Badge
          kind="no_photos"
          tooltip="Do not photograph. The family has asked that no photos or video of this athlete be taken or shared."
        />
      )}
      {flags.waiver_missing && (
        <Badge
          kind="waiver_missing"
          tooltip={
            missing.length > 0
              ? `Not signed yet: ${missing.join(", ")}.`
              : "A required waiver has not been signed for this person."
          }
        />
      )}
    </span>
  );
};

interface BadgesProps extends RosterFlagSubjectRef {
  className?: string;
}

/** One person's badges, read from the surrounding RosterFlagsScope. */
export const RosterFlagBadges: React.FC<BadgesProps> = ({ athleteId, registrationId, className }) => {
  const scope = useContext(RosterFlagsContext);
  if (!scope?.result) return null;
  return <RosterFlagPills flags={scope.flagsFor({ athleteId, registrationId })} className={className} />;
};

/**
 * Wrap one person's row. While the summary line is narrowed to a flag, a row
 * for someone without that flag renders nothing. Outside a scope, or with no
 * filter, it is transparent.
 */
export const RosterFlagRow: React.FC<RosterFlagSubjectRef & { children: React.ReactNode }> = ({
  athleteId,
  registrationId,
  children,
}) => {
  const scope = useContext(RosterFlagsContext);
  if (scope && !scope.matches({ athleteId, registrationId })) return null;
  return <>{children}</>;
};

export default RosterFlagBadges;
